import { Outlet, RouterProvider, useRouteError, createBrowserRouter } from "react-router-dom"
import { I18nProvider } from "./components/route/I18nProvider"
import { AuthGuard } from "./components/route/AuthGuard"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { Suspense, lazy, useEffect, useMemo } from "react"
import { FullSpinner } from "./components/common/Spinner/index.tsx"
import { ErrorRetry } from "./components/route/LoaderLayout.tsx"
import NotFound from "./components/route/NotFound.tsx"
import { HomeLayout } from "./pages/Home/HomeLayout.tsx"

const Home = lazy(() => import("./pages/Home/Home.tsx"))
const Bonds = lazy(() => import("./pages/Home/Bonds.tsx"))
const BondDetail = lazy(() => import("./pages/BondDetail.tsx"))
const Migration = lazy(() => import("./pages/Migration/Migration.tsx"))
const Auth = lazy(() => import("./pages/Auth.tsx"))

const Admin = lazy(() => import("./pages/Home/Admin.tsx"))

function ErrorBoundary() {
  const error = useRouteError()

  const isFetchDynamicallyError = useMemo(() => {
    if (!error) return
    if (typeof error !== "object") return
    if (!("message" in error)) return
    return `${error.message}`.includes("Failed to fetch dynamically imported module") || `${error.message}`.includes("is not a valid JavaScript MIME type")
  }, [error])

  useEffect(() => {
    if (!isFetchDynamicallyError) return
    window.location.reload()
  }, [error, isFetchDynamicallyError])

  if (isFetchDynamicallyError) return <></>
  return <ErrorRetry retry={() => window.location.reload()} err={error} />
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <I18nProvider />,
    children: [
      {
        path: "",
        element: (
          <Suspense fallback={<FullSpinner />}>
            <Outlet />
          </Suspense>
        ),
        errorElement: <ErrorBoundary />,
        children: [
          {
            path: "",
            element: <AuthGuard />,
            children: [
              {
                path: "",
                element: <HomeLayout type="migration" />,
                children: [
                  {
                    path: "",
                    element: <Home />,
                  },
                ],
              },
              {
                path: "bonds",
                children: [
                  {
                    path: "",
                    element: <HomeLayout type="bonds" />,
                    children: [
                      {
                        path: "",
                        element: <Bonds />,
                      },
                    ],
                  },
                  {
                    path: ":id",
                    element: <BondDetail />,
                  },
                ],
              },
              {
                path: "admin",
                children: [
                  {
                    path: "",
                    element: <HomeLayout type="admin" />,
                    children: [
                      {
                        path: "",
                        element: <Admin />,
                      },
                    ],
                  },
                ],
              },
              {
                path: "migration",
                children: [
                  {
                    path: ":id",
                    element: <Migration />,
                  },
                ],
              },
            ],
          },
          {
            path: "auth",
            element: <Auth />,
          },
          {
            path: "*",
            element: <NotFound />,
          },
        ],
      },
    ],
  },
])

const queryClient = new QueryClient()

export const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  )
}

export default App
